import { ChangeDetectorRef, Component, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ReposeDialogComponent } from '@components/repose-setting/repose-dialog/repose-dialog.component';
import { ReposeGroupSocketService } from '@service/repose-group-socket.service';
import { Vessel } from 'icreate/common/models/vessel';
import { firstValueFrom, lastValueFrom, Subject, takeUntil } from 'rxjs';
import _ from 'lodash';
import { ReposeGroupService } from '@service/repose-group.service';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { ReposeGroupHelperService } from '@service/repose-group-helper.service';
import { MessageDialogService } from '@service/message-dialog.service';
import { PrintReposeGroupService } from '@service/print-repose-groups/print-repose-groups.service';
import { ReposeGroup } from 'icreate/common/models/repose-group';
import {
    ConfirmationDialogComponent,
    ConfirmationDialogType,
} from '@components/confirmation-dialog/confirmation-dialog.component';

@Component({
    selector: 'app-repose-setting',
    templateUrl: './repose-setting.component.html',
    styleUrl: './repose-setting.component.css',
    standalone: false,
})
export class ReposeSettingComponent {
    @ViewChild('deleteReposeGroupConfirmation')
    deleteReposeGroupConfirmation: ConfirmationWindowComponent;

    @Input() vessel!: Vessel;
    @Input() userCanEdit: boolean = true;
    @Output() pageUpdated = new EventEmitter<boolean>();

    reposeGroupList: ReposeGroup[];
    initialReposeGroupList: ReposeGroup[];

    isSelectAll: boolean = false;
    reposeGroupPageEditMode: boolean = false;
    isLoading: boolean = true;

    private destroySubscriptionSubject$ = new Subject<void>();

    constructor(
        private reposeGroupSocketService: ReposeGroupSocketService,
        private reposeGroupService: ReposeGroupService,
        private reposeGroupHelperService: ReposeGroupHelperService,
        private dialog: MatDialog,
        private cdr: ChangeDetectorRef,
        private messageDialogService: MessageDialogService,
        private reposePrintService: PrintReposeGroupService
    ) {}

    @HostListener('window:beforeunload', ['$event'])
    async beforeUnload(event) {
        if (this.hasUnsavedChanges()) {
            event.preventDefault();
        }
    }

    async ngOnInit() {
        this.reposeGroupList = await this.reposeGroupService.getAll(this.vessel.id);
        this.initialReposeGroupList = _.cloneDeep(this.reposeGroupList);

        this.reposeGroupSocketService
            .reposeGroupsUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(({ reposeGroups, vesselId }) => {
                if (vesselId === this.vessel.id) {
                    this.reposeGroupList = reposeGroups;
                    this.showMessageDialog('repose-group.message-dialog.update.message', '');
                }
            });

        this.reposeGroupHelperService.loadingEmitter
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(loading => {
                this.isLoading = loading;
                this.cdr.detectChanges();
            });

        this.reposeGroupHelperService.pageUpdateEmitter
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(value => {
                this.onGroupUpdated();
                this.cdr.detectChanges();
            });

        this.isLoading = false;
    }

    async openCreateDialog() {
        if (!this.userCanEdit) return;
        const dialogRef = this.dialog.open(ReposeDialogComponent, {
            data: {
                name: '',
                description: '',
                vesselId: this.vessel.id,
                nonDigitalDataType: '',
                nonDigitalValueFlag: '',
                setPoint: '',
                nonDigitalAlarm: '',
                digitalChannelStatus: '',
                startDelay: 0,
                stopDelay: 0,
                groupNo: this.reposeGroupList.length,
                reposeGroupList: this.reposeGroupList,
            },
            disableClose: true,
        });

        const result = await firstValueFrom(dialogRef.afterClosed());
        if (result) {
            this.reposeGroupList.push(result.reposeGroup);
            this.onGroupUpdated();
        }
    }

    showMessageDialog(message, name) {
        this.messageDialogService.openMessage(message, { name: name });
    }

    async reposeGroupDrop(event) {
        if (!this.userCanEdit) return;
        try {
            this.reposeGroupHelperService.showLoading();
            await this.reposeGroupService.moveItem(event.previousIndex, event.currentIndex, this.vessel.id);
        } catch (error) {
            console.log(error);
        } finally {
            this.reposeGroupHelperService.hideLoading();
        }
    }

    async printGroups() {
        if (this.hasUnsavedChanges()) {
            const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
                width: '474px',
                data: {
                    type: ConfirmationDialogType.COMPLEX,
                    title: 'confirmation-dialog.print.title',
                    message: 'confirmation-dialog.print.message',
                    backButton: 'confirmation-dialog.print.back',
                    discardButton: 'confirmation-dialog.print.discard',
                    saveButton: 'confirmation-dialog.print.save',
                },
            });
            const confirmationOption = await lastValueFrom(dialogRef.afterClosed());

            if (confirmationOption === ConfirmationWindowTypes.GO_BACK) {
                return;
            } else if (confirmationOption === ConfirmationWindowTypes.SAVE) {
                await this.save();
            }
        }

        try {
            await this.reposePrintService.triggerPrint(this.vessel, true);
        } catch (err) {
            console.error('Error in printSensorCableTable:', err);
            throw err;
        }
    }

    hasUnsavedChanges() {
        return this.userCanEdit && !_.isEqual(this.initialReposeGroupList, this.reposeGroupList);
    }

    async save() {
        if (!this.userCanEdit) return;
        this.isLoading = true;
        try {
            await this.reposeGroupService.saveAll(this.reposeGroupList, this.vessel.id);
            this.initialReposeGroupList = _.cloneDeep(this.reposeGroupList);
            this.pageUpdated.emit(false);
        } catch (error) {
            console.log(error);
            throw new Error(error);
        } finally {
            this.isLoading = false;
        }
    }

    onGroupUpdated() {
        !_.isEqual(this.initialReposeGroupList, this.reposeGroupList)
            ? this.pageUpdated.emit(true)
            : this.pageUpdated.emit(false);
    }

    async deleteRow(index) {
        if (!this.userCanEdit) return;
        this.reposeGroupList.splice(index, 1);
        this.onGroupUpdated();
    }
}
